import { getIdpAccessToken } from './token-store';

/**
 * Links para apps de fora que levam o token da sessao na URL: Retool "Sapron
 * Labs" (`?token=`) e GeoSeazone (`#sapron_token=`).
 *
 * Token na URL e proibido pelo ADR 005 do `seazone-idp`; estes dois links sao
 * a excecao registrada nele (22/10/2026). Com sessao do IDP o link leva um
 * access token recem-renovado (5 min inteiros), nunca o que ja esta perto de
 * vencer. Nao estender a outro app.
 */

/** Renova a sessao do IDP e devolve o access token novo; `undefined` se falhou. */
export type RenovarTokenParaLink = () => Promise<string | undefined>;

interface FontesDoToken {
  /** Token da perspectiva (`?mode=view`), quando o link a respeita. */
  readonly perspectiva?: string | null;
  /** Access token da sessao legada (`localStorage`). */
  readonly legado?: string | null;
  readonly renovar: RenovarTokenParaLink;
}

/**
 * Mesma ordem de `services/request.ts`: perspectiva > IDP > legado. Com sessao
 * do IDP, a falha da renovacao sai sem token — o legado ali pode ser sobra de
 * outro login nesta maquina, e mandar a identidade errada e pior que mandar
 * nenhuma (o Retool pede login; o mapa abre anonimo).
 */
export async function tokenParaLink({
  perspectiva,
  legado,
  renovar,
}: FontesDoToken): Promise<string | undefined> {
  if (perspectiva) return perspectiva;

  if (getIdpAccessToken() !== undefined) {
    try {
      return await renovar();
    } catch {
      return undefined;
    }
  }

  return legado || undefined;
}

/**
 * Abre a aba ja no clique e so navega depois de resolver a URL. `window.open`
 * depois de um `await` perde o gesto do usuario e o Safari bloqueia como
 * pop-up; abrir antes e atribuir `location` depois nao. O `opener` e cortado a
 * mao porque `noopener` faria o `window.open` devolver `null`. Com a aba
 * bloqueada ja no clique, outro `window.open` depois do `await` tambem seria:
 * o link abre na aba atual.
 */
export async function abrirEmNovaAba(
  urlSemToken: string,
  resolverUrl: () => Promise<string>,
): Promise<void> {
  const aba = window.open('', '_blank');
  if (aba) aba.opener = null;

  const destino = await resolverUrl().catch(() => urlSemToken);

  if (aba) {
    aba.location.href = destino;
    return;
  }
  window.location.assign(destino);
}
